import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, MessageCircle, Send } from "lucide-react";
import { Logo } from "./Logo";
import { SITE, whatsappLink } from "../data/config";
import { submitLead } from "../lib/submit";
import { toast } from "sonner";

const COLS = [
  { titleKey: "colPlatform", links: [["plans", "/plans"], ["smartbag", "/smartbag"], ["telemedicine", "/telemedicine"], ["connectivity", "/connectivity"], ["riskOptions", "/risk-options"]] },
  { titleKey: "colWho", links: [["traders", "/traders-conferences"], ["seasonal", "/seasonal-workers"], ["partners", "/partners"], ["getQuote", "/quote"], ["contact", "/contact"]] },
  { titleKey: "colTrust", links: [["compliance", "/compliance"], ["faq", "/faq"]] },
];

const LABELS = {
  plans: "nav.plans", smartbag: "nav.smartbag", telemedicine: "nav.telemedicine",
  connectivity: "nav.connectivity", riskOptions: "nav.riskOptions", traders: "nav.traders",
  seasonal: "seasonal.overline", partners: "nav.partners", getQuote: "common.getQuote",
  contact: "nav.contact", compliance: "nav.compliance", faq: "nav.faq",
};

export function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error(t("footer.newsletterErr"));
      return;
    }
    const res = await submitLead("newsletter", { source: "r0am_website", lead_type: "newsletter", email });
    if (!res.ok) return toast.error(t("footer.newsletterErr"));
    toast.success(t("footer.newsletterOk"));
    setEmail("");
  };

  return (
    <footer className="relative border-t border-white/10 bg-navy-900 grain" data-testid="site-footer">
      <div className="container-x py-16">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Logo size="md" testId="footer-logo" />
            <p className="mt-5 text-white/55 text-sm max-w-xs leading-relaxed">{t("footer.tagline")}</p>
            <p className="mt-4 text-gold/80 text-xs uppercase tracking-[0.2em]" data-testid="footer-pronounced">
              {t("footer.pronouncedLine", { pronounced: t("common.pronounced") })}
            </p>

            <form onSubmit={subscribe} className="mt-6 flex gap-2 max-w-sm" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("footer.newsletterPh")}
                className="flex-1 bg-navy-800 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-signal"
                data-testid="newsletter-input"
              />
              <button type="submit" className="bg-white/10 hover:bg-white/20 border border-white/10 rounded-md px-4 transition-colors" aria-label="Subscribe" data-testid="newsletter-submit">
                <Send size={16} />
              </button>
            </form>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {COLS.map((col) => (
              <div key={col.titleKey}>
                <h4 className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">{t(`footer.${col.titleKey}`)}</h4>
                <ul className="space-y-3">
                  {col.links.map(([key, to]) => (
                    <li key={to + key}>
                      <Link to={to} className="text-sm text-white/65 hover:text-white transition-colors" data-testid={`footer-link-${key}`}>
                        {t(LABELS[key])}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">{t("footer.reachUs")}</h4>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 text-sm text-white/65 hover:text-white mb-3" data-testid="footer-email">
              <Mail size={15} /> {SITE.email}
            </a>
            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/65 hover:text-white" data-testid="footer-whatsapp">
              <MessageCircle size={15} /> {t("footer.whatsappSupport")}
            </a>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <p className="text-xs text-white/40 max-w-2xl leading-relaxed">
            {t("footer.disclaimer")}{" "}
            <Link to="/compliance" className="text-white/60 underline hover:text-white">{t("footer.seeCompliance")}</Link>.
          </p>
          <p className="text-xs text-white/40">© {new Date().getFullYear()} Wadiah Mutual</p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
